<template>
    <div>
        <v-card class="rounded-lg fill-height">
            <v-divider></v-divider>
            <v-card-title>HighLevel and Agent CRM Integrations
                <v-btn text :href="highlevelRegistrationUrl" target="_blank" icon>
                    <v-icon>mdi-open-in-new</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text>
                <p>
                    If you have a HighLevel or Agent CRM <a :href="highlevelRegistrationUrl" target="_blank">account</a>, you can connect it here in order to handle processing of meeting recordings. 
                    Look for Video Conference custom fields to use in building your workflows.
                </p>
                <v-progress-linear indeterminate v-if="loading"></v-progress-linear>
                <v-row>
                    <v-col v-for="item in integrations" :key="item.locationId" cols="12" sm="6" lg="3" xl="2" class="d-flex flex-column">
                        <v-card class="elevation-5 flex d-flex flex-column rounded-lg">
                            <v-card-title style="background-color:#fff;min-height:120px;" class="pa-4" min-height="120">
                                <v-img v-if="item.location && item.location.logoUrl" :src="item.location.logoUrl" contain max-height="75"></v-img>
                            </v-card-title>
                            <v-card-title class="font-weight-bold">
                                <span v-if="item.location" class="pr-2">{{ item.location ? item.location.name : 'Location' }}</span>
                            </v-card-title>
                            <v-card-subtitle>
                                <template v-if="item.room_name">
                                    {{ item.room_name }}
                                    <v-btn icon x-small @click="showConnectRoomDialog(item)"><v-icon>mdi-pencil</v-icon></v-btn>
                                </template>
                                <v-btn v-else x-small color="primary" text outlined @click="showConnectRoomDialog(item)">Connect Room</v-btn>
                            </v-card-subtitle>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn @click="confirmDeleteIntegration(item)" x-small color="red" icon title="Delete">
                                    <v-icon>mdi-trash-can-outline</v-icon>
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="pb-4">
                <v-spacer></v-spacer>
                <v-btn class="mt-8" :href="highLevelOauthUrl" color="primary">Add Integration</v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>
        </v-card>
        <v-dialog
            width="60%"
            max-width="500"
            v-model="show_confirm_delete_dialog"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Delete Integration?
                </v-card-title>
                <v-card-text>
                    Are you sure you want to delete <strong>{{delete_integration.location ? delete_integration.location.name : 'this connection'}}</strong>?
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        text
                        @click="show_confirm_delete_dialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="red"
                        @click="deleteIntegration()"
                        :loading="loading"
                        :disabled="loading"
                    >
                        <v-icon>mdi-delete</v-icon> Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            width="60%"
            max-width="500"
            v-model="show_connect_room_dialog"
        >
            <v-card>
                <v-card-title class="text-h5">
                    Connect Room
                </v-card-title>
                <v-card-text>
                    To fully integrate, select the room you want to use for this integration. 
                    This will be the room used in the links that are auto-created for your meetings.

                    <v-select
                        v-model="selected_room"
                        :items="rooms.rooms"
                        item-text="post_title"
                        item-value="ID"
                        outlined
                        dense
                        class="mt-2"
                    ></v-select>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        text
                        @click="show_connect_room_dialog = false"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="success"
                        @click="connectRoom()"
                        :loading="connecting_room"
                        :disabled="connecting_room"
                    >
                        Connect
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>


<script>
import { useUserStore } from '../../stores/user'
// import { useAppStore } from '../../stores/app'
import { useRoomsStore } from '../../stores/rooms'

export default {
    name: 'Integrations',
    created: function() {
        this.user = useUserStore()
        this.rooms = useRoomsStore()
        this.rooms.loadRooms()
        // this.app = useAppStore()
    },
    data: function() {
        return {
            user: null,
            // app: null,
            rooms: [],
            loading: false,
            saving: false,
            integrations: [],
            headers: [    
                {
                    text: 'Integration',
                    align: 'start',
                    sortable: true,
                    value: 'locationId',
                },
                {
                    text: '',
                    value: 'actions',
                    align: 'right',
                    sortable: false
                }
            ],
            show_confirm_delete_dialog: false,
            delete_integration: { location: { name: '' } },
            show_connect_room_dialog: false,
            selected_room: 0,
            connecting_integration: null,
            connecting_room: false,
        }
    },
    mounted: function() {
        // this.loadSettings()
        if(window.location.search.indexOf('?code=') > -1) {
			//get the HighLevel access tokens and save to this account
			let url = new URL(window.location.href)
			let params = new URLSearchParams(url.search)
			let code = params.get('code')
			this.getHighLevelTokens(code)
		} else {
            this.loadIntegrations()
        }
    },
    computed: {
        domain: function() {
            return window.location.host
        },
        highLevelOauthUrl: function() {
            let client_id = this.user?.app?.site?.domain_settings?.settings?.domain.meta?.highlevel_client_id[0]
            return `https://marketplace.gohighlevel.com/oauth/chooselocation?response_type=code&redirect_uri=https://${window.location.host}/account/&client_id=${client_id}&scope=contacts.readonly%20contacts.write%20locations.readonly%20locations/customFields.readonly%20locations/customFields.write%20locations/customValues.readonly%20locations/customValues.write%20users.readonly%20users.write`
        },
        highlevelRegistrationUrl: function() {
            return this.user?.app?.site?.domain_settings?.settings?.domain.meta?.highlevel_registration_url ? this.user?.app?.site?.domain_settings?.settings?.domain.meta?.highlevel_registration_url[0] : 'https://www.gohighlevel.com/?fp_ref=chad-wyatt44'
        }
    },
    methods: {
        showConnectRoomDialog(integration) {
            this.connecting_integration = integration
            this.selected_room = integration.room_id ?? null
            this.show_connect_room_dialog = true
        },
        connectRoom() {
            if(!this.selected_room) {
                this.user.app.showError("Select a room to connect.")
                return
            }
            this.connecting_room = true
            this.user.app.post('highlevel/integrations/connect_room', 
                {
                    integration: this.connecting_integration, 
                    room_id: this.selected_room
                }
            ).then(response => {
                this.integrations = response.data
                this.show_connect_room_dialog = false
                this.connecting_room = false
            }).catch(error => {
                this.user.app.showError(error.message)
                this.connecting_room = false
                // this.show_connect_room_dialog = false
            })
        },
        getHighLevelTokens(code) {
            this.loading = true
            this.user.app.get(`highlevel/token/${code}`)
                .then(response => {
                    this.user.app.showSuccess('Connection Successful')
                    this.integrations = response.data
                    this.loading = false

                    // loop through response and find match on code value
                    for (let integration of this.integrations) {
                        if (integration.code === code) {
                            this.showConnectRoomDialog(integration)
                        }
                    }
                })
                .catch(error => {
                    console.error(error)
                    //+error.response.data.error_description
                    this.user.app.showError('Connection Failed')
                    this.loadIntegrations()
                })
                // .catch(this.loading = false)
                // .finally(this.removeCodeFromUrl())
        },
        removeCodeFromUrl() {
            // Remove the 'code' parameter
            const url = new URL(window.location.href);
            const params = new URLSearchParams(url.search);
            params.delete('code');
            const newUrl = url.origin + url.pathname + '?' + params.toString() + url.hash;
            window.location.href = new URL(newUrl).toString();
        },
        confirmDeleteIntegration(item) {
            this.delete_integration = item
            this.show_confirm_delete_dialog = true
        },
        deleteIntegration() {
            this.loading = true
            this.user.app.delete(`highlevel/integrations/${this.delete_integration.locationId}`)
                .then(response => {
                    this.integrations = response.data
                    this.loading = false
                })
                .finally(() => this.show_confirm_delete_dialog = false)
        },
        loadIntegrations() {
            this.loading = true
            this.user.app.get('highlevel/integrations')
            .then(response => {
                this.integrations = response.data
            })
            .finally(() => this.loading = false)
        },
    }
}
</script>